import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '../../api/admin';
import { useAsync, Banner, Empty } from './ui';
import MetricChart from '../../components/MetricChart';
import Sparkline from '../../components/Sparkline';
import BarChart from '../../components/BarChart';
import { FIELD_META, seriesFor, deriveColumn, type Point } from '../../lib/readingColumns';
import { resolveThreshold, severityOf } from '../../lib/thresholds';
import { pmsGroupLabel, groupBy } from '../../lib/pmsGroup';

type Row = Record<string, unknown>;
interface Metric { key: string; label: string; section: string; unit: string; col: string }

const PRESETS: Array<[string, number]> = [['24 h', 1], ['7 days', 7], ['30 days', 30], ['90 days', 90]];
const iso = (d: Date) => d.toISOString().slice(0, 10);
const daysAgo = (n: number) => iso(new Date(Date.now() - n * 86400000));
const fmtVal = (v: number) => (Math.abs(v) >= 1000 ? Math.round(v).toLocaleString() : Math.round(v * 100) / 100);

function fmtGap(ms: number): string {
  if (ms <= 0) return '—';
  const m = Math.round(ms / 60000);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60), r = m % 60;
  if (h < 48) return r ? `${h}h ${r}m` : `${h}h`;
  return `${Math.round(h / 24)} d`;
}

export default function TrendsAdmin() {
  const meta = useAsync(async () => {
    const t = await adminApi.trends();
    return { equip: t.equipment, templates: t.templates, thresholds: t.thresholds, config: t.config as Record<string, unknown> };
  }, [], 'trends-meta');

  const equipRows = meta.data?.equip ?? [];
  const [catFilter, setCatFilter] = useState<'daily' | 'pms'>('daily');
  const tplTitleById = useMemo(() => new Map((meta.data?.templates ?? []).map((t) => [t.templateId, t.title])), [meta.data]);
  const dailyEquip = useMemo(() => equipRows.filter((r) => String(r['Category'] ?? 'daily') !== 'pms'), [equipRows]);
  const pmsEquip = useMemo(() => equipRows.filter((r) => String(r['Category'] ?? 'daily') === 'pms'), [equipRows]);
  const pmsEquipGroups = useMemo(
    () => groupBy(pmsEquip, (r) => pmsGroupLabel(tplTitleById.get(String(r['Template ID'])) ?? String(r['Template ID'])) || 'Other'),
    [pmsEquip, tplTitleById],
  );
  const shownEquipRows = catFilter === 'daily' ? dailyEquip : pmsEquip;

  const [equipmentId, setEquipmentId] = useState('');
  const [preset, setPreset] = useState(7);
  const [customFrom, setCustomFrom] = useState(daysAgo(7));
  const [customTo, setCustomTo] = useState(daysAgo(0));
  const custom = preset === 0;
  const range = custom ? { from: customFrom, to: customTo } : { from: daysAgo(preset), to: daysAgo(0) };

  useEffect(() => {
    if (!shownEquipRows.some((r) => String(r['Equipment ID']) === equipmentId) && shownEquipRows.length) {
      setEquipmentId(String(shownEquipRows[0]['Equipment ID']));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownEquipRows]);

  const [applied, setApplied] = useState<{ equipmentId: string; from: string; to: string } | null>(null);
  useEffect(() => {
    if (equipmentId) setApplied({ equipmentId, ...range });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipmentId, preset]);

  const readings = useAsync(
    () => (applied ? adminApi.listReadings({ equipmentId: applied.equipmentId, from: applied.from, to: applied.to }, 2000) : Promise.resolve({ ok: true as const, rows: [] as Row[] })),
    [applied],
    applied ? `trend-readings:${applied.equipmentId}:${applied.from}:${applied.to}` : undefined,
  );
  const rows = readings.data?.rows ?? [];

  const selEquip = equipRows.find((r) => String(r['Equipment ID']) === equipmentId);
  const templateId = selEquip ? String(selEquip['Template ID']).trim() : '';

  const metrics: Metric[] = useMemo(() => {
    const t = meta.data?.templates.find((x) => x.templateId === templateId);
    if (!t) return FIELD_META.map((f) => ({ key: f.key, label: f.label, section: f.section, unit: f.unit, col: f.col }));
    return t.fields.filter((f) => f.dataType === 'decimal' || f.dataType === 'int').map((f) => ({
      key: f.fieldKey, label: f.label || f.fieldKey, section: f.sectionLabel || f.sectionKey,
      unit: f.unit, col: f.col || deriveColumn(f.fieldKey, f.sectionKey, f.label),
    }));
  }, [meta.data, templateId]);

  const [selectedKey, setSelectedKey] = useState('');
  useEffect(() => { setSelectedKey(metrics[0]?.key ?? ''); }, [metrics]);
  const selected = metrics.find((m) => m.key === selectedKey) ?? metrics[0];

  const seriesByKey = useMemo(() => {
    const m: Record<string, Point[]> = {};
    metrics.forEach((f) => { m[f.key] = seriesFor(rows, f.col); });
    return m;
  }, [metrics, rows]);

  const thresholdFor = (key: string) =>
    resolveThreshold(meta.data?.thresholds ?? [], equipmentId, templateId, key);

  // ---- KPIs ----
  const kpi = useMemo(() => {
    const ts = rows.map((r) => Date.parse(String(r['Effective Reading At'] ?? r['Server Timestamp'] ?? ''))).filter((n) => !Number.isNaN(n)).sort((a, b) => a - b);
    let gap = 0;
    for (let i = 1; i < ts.length; i++) gap = Math.max(gap, ts[i] - ts[i - 1]);
    const abnormal = rows.filter((r) => {
      const s = String(r['Abnormal Status'] ?? '').toLowerCase();
      return s === 'warning' || s === 'critical';
    });
    const critical = abnormal.filter((r) => String(r['Abnormal Status']).toLowerCase() === 'critical').length;

    const targetMin = Number(selEquip?.['Target Interval (min)']) || Number(meta.data?.config?.targetIntervalMin) || 120;
    const fromMs = Date.parse(range.from + 'T00:00:00');
    const toMs = Date.parse(range.to + 'T23:59:59');
    const expected = Math.max(1, Math.floor((toMs - fromMs) / (targetMin * 60000)));
    const compliance = Math.min(100, Math.round((rows.length / expected) * 100));

    return { count: rows.length, gap, abnormal: abnormal.length, critical, compliance };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, selEquip, meta.data]);

  const perDay = useMemo(() => {
    const m = new Map<string, number>();
    rows.forEach((r) => { const d = String(r['Date'] ?? ''); if (d) m.set(d, (m.get(d) ?? 0) + 1); });
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([label, value]) => ({ label: label.slice(5), value }));
  }, [rows]);

  const sel = selected ? seriesByKey[selected.key] ?? [] : [];
  const selStats = useMemo(() => {
    if (!sel.length) return null;
    const vs = sel.map((p) => p.v);
    const avg = vs.reduce((a, b) => a + b, 0) / vs.length;
    const th = selected ? thresholdFor(selected.key) : null;
    const bad = sel.filter((p) => severityOf(p.v, th) !== 'normal').length;
    return { min: Math.min(...vs), max: Math.max(...vs), avg, last: vs[vs.length - 1], bad };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel, selected, meta.data]);

  const grouped = useMemo(() => {
    const g: Record<string, Metric[]> = {};
    metrics.forEach((m) => { (g[m.section] ??= []).push(m); });
    return g;
  }, [metrics]);

  return (
    <div className="trends">
      <h1>Trends &amp; charts</h1>

      <div className="choice-group" role="tablist" aria-label="Equipment type" style={{ marginBottom: 12 }}>
        <button type="button" className={`choice-btn ${catFilter === 'daily' ? 'on' : ''}`} role="tab" aria-selected={catFilter === 'daily'} onClick={() => setCatFilter('daily')}>
          Daily readings ({dailyEquip.length})
        </button>
        <button type="button" className={`choice-btn ${catFilter === 'pms' ? 'on' : ''}`} role="tab" aria-selected={catFilter === 'pms'} onClick={() => setCatFilter('pms')}>
          PMS checks ({pmsEquip.length})
        </button>
      </div>
      <div className="trend-controls">
        <select value={equipmentId} onChange={(e) => setEquipmentId(e.target.value)}>
          {catFilter === 'daily' && dailyEquip.map((r, i) => <option key={i} value={String(r['Equipment ID'])}>{String(r['Equipment Name'])}</option>)}
          {catFilter === 'pms' && pmsEquipGroups.map((g) => (
            <optgroup key={g.label} label={g.label}>
              {g.items.map((r, i) => <option key={i} value={String(r['Equipment ID'])}>{String(r['Equipment Name'])}</option>)}
            </optgroup>
          ))}
        </select>
        <div className="segmented">
          {PRESETS.map(([label, d]) => (
            <button key={d} className={preset === d ? 'on' : ''} onClick={() => setPreset(d)}>{label}</button>
          ))}
          <button className={custom ? 'on' : ''} onClick={() => setPreset(0)}>Custom</button>
        </div>
        {custom && (
          <div className="trend-custom">
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
            <span>→</span>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
            <button className="sm" onClick={() => setApplied({ equipmentId, from: customFrom, to: customTo })}>Apply</button>
          </div>
        )}
      </div>

      <Banner error={meta.error || readings.error} />
      {(meta.loading || readings.loading) && <div className="spinner" />}

      {applied && !readings.loading && (
        <>
          <div className="kpiRow">
            <div className="kpi"><div className="kpiN">{kpi.count}</div><div className="kpiL">Readings</div></div>
            <div className={`kpi ${kpi.compliance < 70 ? 'bad' : kpi.compliance < 90 ? 'warn' : ''}`}>
              <div className="kpiN">{kpi.compliance}%</div><div className="kpiL">On-schedule</div>
            </div>
            <div className={`kpi ${kpi.critical ? 'bad' : kpi.abnormal ? 'warn' : ''}`}>
              <div className="kpiN">{kpi.abnormal}</div><div className="kpiL">Abnormal{kpi.critical ? ` · ${kpi.critical} critical` : ''}</div>
            </div>
            <div className="kpi"><div className="kpiN">{fmtGap(kpi.gap)}</div><div className="kpiL">Longest gap</div></div>
          </div>

          {rows.length === 0 && <Empty icon="📉">No readings for this equipment in the selected range.</Empty>}

          {rows.length > 0 && (
            <>
              <h2>Metrics</h2>
              {Object.entries(grouped).map(([section, list]) => (
                <div key={section}>
                  <div className="section-label">{section}</div>
                  <div className="metric-grid">
                    {list.map((m) => {
                      const pts = seriesByKey[m.key] ?? [];
                      const th = thresholdFor(m.key);
                      const last = pts[pts.length - 1]?.v;
                      const first = pts[0]?.v;
                      const delta = last != null && first != null && first !== 0 ? ((last - first) / Math.abs(first)) * 100 : null;
                      const sev = last != null ? severityOf(last, th) : 'normal';
                      return (
                        <button key={m.key} className={`metric-card ${selectedKey === m.key ? 'on' : ''}`} onClick={() => setSelectedKey(m.key)}>
                          <div className="mc-top">
                            <span>{m.label}</span>
                            {sev !== 'normal' && <span className={`sevdot ${sev}`} />}
                          </div>
                          <div className="mc-value">{last != null ? fmtVal(last) : '—'}<span className="mc-unit">{m.unit}</span></div>
                          <Sparkline points={pts} threshold={th} />
                          <div className="mc-foot">
                            <span>{delta == null ? '—' : delta > 1 ? `▲ ${delta.toFixed(0)}%` : delta < -1 ? `▼ ${Math.abs(delta).toFixed(0)}%` : 'steady'}</span>
                            <span className="muted">{pts.length} pts</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {selected && (
                <div className="card metric-detail">
                  <div className="spread">
                    <h3 style={{ margin: 0 }}>{selected.label}{selected.unit ? ` (${selected.unit})` : ''}</h3>
                    {(() => {
                      const th = thresholdFor(selected.key);
                      return th ? (
                        <span className="tiny muted">
                          normal {th.normalMin ?? '–'}–{th.normalMax ?? '–'} · warn {th.warnMin ?? '–'}–{th.warnMax ?? '–'}
                        </span>
                      ) : <span className="tiny muted">no thresholds set</span>;
                    })()}
                  </div>
                  <MetricChart points={sel} unit={selected.unit} threshold={thresholdFor(selected.key)} />
                  {selStats && (
                    <div className="metric-stats">
                      <span>min <strong>{fmtVal(selStats.min)}</strong></span>
                      <span>avg <strong>{fmtVal(selStats.avg)}</strong></span>
                      <span>max <strong>{fmtVal(selStats.max)}</strong></span>
                      <span>last <strong>{fmtVal(selStats.last)}</strong></span>
                      <span className={selStats.bad ? 'warn' : ''}>{selStats.bad} out of range</span>
                    </div>
                  )}
                </div>
              )}

              <h2>Reading frequency</h2>
              <div className="card"><BarChart data={perDay} /></div>
            </>
          )}
        </>
      )}
    </div>
  );
}
